import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Pencil, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { ContactLinks } from "@/components/contacts/ContactLinks";
import { ContactsDialog } from "@/components/travel/ContactsDialog";
import { Avatar } from "@/components/friends/Avatar";
import { DockSheet, SearchBox, plain } from "./DockSheet";
import { InviteContact, InviteStatus } from "@/components/contacts/InviteContact";
import { PrivateNote } from "@/components/contacts/PrivateNote";
import { PhoneNudge } from "@/components/contacts/PhoneNudge";
import { PersonActions } from "@/components/contacts/PersonActions";
import { splitProfessions } from "@/lib/professions";

const NONE = "No profession added";

// One entry per person: copies saved under several places are grouped by their pid
const useTravelFriends = (data) => {
  const { data: all = [] } = useQuery({ queryKey: ["friends"], queryFn: api.friends });
  const friends = useMemo(() => all.filter((f) => f.mutual), [all]);
  const people = useMemo(() => {
    const byPid = {};
    data.wishlist.forEach((w, idx) => w.contacts.forEach((c, i) => {
      const key = c.pid || `n-${plain(c.name)}`;
      (byPid[key] ||= { key, kind: "contact", copies: [] }).copies.push({ c, w, idx, i });
    }));
    const fById = Object.fromEntries(friends.map((f) => [f.friend_id, f]));
    const contacts = Object.values(byPid).map((p) => {
      const { c } = p.copies[0];
      const friendId = p.copies.find((x) => x.c.friend_id)?.c.friend_id || "";
      return { ...p, name: c.name, profession: c.profession || "", c, friendId, f: fById[friendId],
        places: p.copies.map(({ c: x, w }) => (x.city ? `${x.city}, ${w.country}` : w.country)) };
    });
    const linked = new Set(contacts.map((p) => p.friendId).filter(Boolean));
    return [
      ...contacts,
      ...friends.filter((f) => !linked.has(f.friend_id)).map((f) => ({ key: `f-${f.friend_id}`, kind: "friend", name: f.name, profession: f.profession || "", f, places: [] })),
    ].sort((a, b) => a.name.localeCompare(b.name));
  }, [data.wishlist, friends]);
  return { people, friends };
};

const Initial = ({ name }) => <span className="w-9 h-9 rounded-full bg-amber-400/10 text-amber-200 grid place-items-center text-xs font-semibold shrink-0">{name[0]?.toUpperCase()}</span>;

const ProfileLink = ({ id, testId }) => (
  <Link to={`/friend/${id}`} className="flex items-center gap-1 text-xs text-sky-300 hover:text-sky-200 shrink-0" data-testid={testId}>Profile <ArrowUpRight size={13} /></Link>
);

const Row = ({ p, b, ctx }) => {
  const first = p.copies?.[0];
  const sub = [p.profession, p.kind === "friend" ? "Friend on the bucketlist" : p.places.join(" · ")].filter(Boolean).join(" · ");
  return (
    <li className="py-2.5 border-b border-white/5" data-testid={`travel-friend-${p.key}`}>
      <div className="flex items-center gap-3">
        {p.f ? <Avatar f={p.f} size="w-9 h-9" /> : <Initial name={p.name} />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{p.name}</p>
          {p.kind === "contact" && (p.f ? <p className="text-[11px] text-sky-300/80 truncate" data-testid={`travel-friend-linked-${p.key}`}>Linked to {p.f.name}'s profile</p> : <InviteStatus c={p.c} testId={`travel-friend-status-${p.key}`} />)}
        </div>
        {p.kind === "friend" ? <ProfileLink id={p.f.friend_id} testId={`travel-friend-open-${p.key}`} /> : (
          <>
            <ContactLinks c={p.c} testId={`travel-friend-links-${p.key}`} />
            {p.f ? <ProfileLink id={p.friendId} testId={`travel-friend-open-${p.key}`} /> : <InviteContact c={p.c} wishId={first.w.id} index={first.i} testId={`travel-friend-invite-${p.key}`} />}
            <ContactsDialog w={first.w} b={b} index={first.idx + 1} editAt={first.i}>
              <button className="p-1.5 text-slate-500 hover:text-amber-300" aria-label={`Edit ${p.name}`} data-testid={`travel-friend-edit-${p.key}`}><Pencil size={14} /></button>
            </ContactsDialog>
            <PersonActions p={p} {...ctx} testId={`travel-friend-more-${p.key}`} />
          </>
        )}
      </div>
      {sub && <p className="mt-1 pl-12 text-xs text-slate-500 break-words" data-testid={`travel-friend-places-${p.key}`}>{sub}</p>}
    </li>
  );
};

export const TravelFriendsSheet = ({ open, onOpenChange, mode, data, b, names }) => {
  const [q, setQ] = useState("");
  const { people, friends } = useTravelFriends(data);
  const ctx = useMemo(() => ({ people: people.filter((p) => p.kind === "contact"), friends, names }), [people, friends, names]);
  const byProf = mode === "profession";
  const shown = useMemo(() => {
    const t = plain(q.trim());
    return people.filter((p) => !t || plain(byProf ? p.profession : `${p.name} ${p.profession} ${p.places.join(" ")}`).includes(t));
  }, [people, q, byProf]);
  const groups = useMemo(() => {
    if (!byProf) return [["", shown]];
    // Someone tagged "Influencer, Celebrity" is listed under both; groups ignore case/accents
    const t = plain(q.trim());
    const m = {};
    shown.forEach((p) => {
      const profs = splitProfessions(p.profession);
      (profs.length ? profs : [NONE]).filter((x) => x === NONE || !t || plain(x).includes(t)).forEach((x) => {
        (m[plain(x)] ||= { label: x, list: [] }).list.push(p);
      });
    });
    return Object.values(m).map((g) => [g.label, g.list]).sort(([a], [z]) => (a === NONE) - (z === NONE) || a.localeCompare(z));
  }, [shown, byProf, q]);
  return (
    <DockSheet open={open} onOpenChange={onOpenChange} testId={byProf ? "profession-sheet" : "contacts-sheet"}
      title={byProf ? "Profession" : "Contacts"}
      description={byProf ? "Find your travel friends by what they do." : `${people.length} travel friend${people.length === 1 ? "" : "s"} A–Z — your saved contacts and friends on the app.`}>
      <SearchBox value={q} onChange={setQ} placeholder={byProf ? "Search a profession, e.g. chef, guide…" : "Search names, professions or places…"} testId={byProf ? "profession-search-input" : "contacts-search-input"} />
      <PrivateNote className="mt-3" testId={byProf ? "profession-private-note" : "contacts-private-note"} />
      {!byProf && <div className="mt-3"><PhoneNudge testId="contacts-phone-nudge" /></div>}
      <div className="mt-3 flex-1 overflow-y-auto pr-1" data-testid={byProf ? "profession-list" : "contacts-list"}>
        {groups.map(([g, list]) => (
          <section key={g || "all"} data-testid={g ? `profession-group-${plain(g).replace(/[^a-z0-9]+/g, "-")}` : undefined}>
            {g && <h3 className={`sticky top-0 z-10 bg-[#0E131D] pt-3 pb-1 text-xs uppercase tracking-wider ${g === NONE ? "text-slate-500" : "text-sky-300/90"}`}>{g} <span className="text-slate-600 normal-case">· {list.length}</span></h3>}
            <ul>{list.map((p) => <Row key={`${g}-${p.key}`} p={p} b={b} ctx={ctx} />)}</ul>
          </section>
        ))}
        {!shown.length && (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="travel-friends-empty">
            {people.length ? (byProf ? "Nobody with that profession yet." : "No one matches that search.") : "No travel friends yet — tap the green plus to add one."}
          </p>
        )}
      </div>
    </DockSheet>
  );
};
